import cn from 'classnames';
import classNames from 'classnames';
import { compact, isEmpty } from 'lodash';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { ScheduleParting, targetingConstants } from 'sonata-platform-components';
import { BoxV2, showNotification, TitleBar, useDidUpdate, usePrevious } from 'sonata-react-components';
import { isCTVCampaign, isDigitalCampaign, isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { CampaignFamilyEnum, MediaTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { AdvertisingPlatform } from '@/components/common/AdvertisingPlatform/AdvertisingPlatform';
import { AppAndSiteMarketplace } from '@/components/common/AppAndSiteMarketplace/AppAndSiteMarketplace';
import { CampaignMarketplace } from '@/components/common/CampaignMarketplace/CampaignMarketplace';
import { ContextualBidding } from '@/components/common/ContextualBidding/ContextualBidding';
import { ContextualMacros } from '@/components/common/ContextualMacros/ContextualMacros';
import ConversionTracking from '@/components/common/ConversionTracking/ConversionTracking';
import { DynamicCreativeOptimization } from '@/components/common/DynamicCreativeOptimization';
import { MarketplaceDistribution } from '@/components/common/MarketplaceDistribution/MarketplaceDistribution';
import { MultiFlightMap } from '@/components/common/MultiFlightMap';
import { PricingModel } from '@/components/common/PricingModel';
import { Retargeting } from '@/components/common/Retargeting/Retargeting';
import { ScheduleAndDistribution } from '@/components/common/ScheduleAndDistributionSection/ScheduleAndDistribution';
import { SettingsSection } from '@/components/common/settingsSection/SettingsSection';
import { TargetingSection } from '@/components/common/TargetingSection/TargetingSection';
import { VolumetryAndForecastIO } from '@/components/common/VolumetryAndForecast/VolumetryAndForecast';
import { FlightSettings } from '@/components/ioSettings/InsertionOrderForm/FlightSettings/FlighSettings.tsx';
import {
  AppAndSiteChannel,
  AppAndSitesCategories,
  AudienceTargeting,
  ContentCategories,
  Demography,
  DeviceTargeting,
  LQIQualityAssurance,
  MediaQualityAssuranceSystem,
  MobileDeviceAndConnectivity,
  SupplySource,
  TemperatureAndWeather
} from '@/components/Targeting';
import { hasContentCategoriesPermission } from '@/components/Targeting/ContentCategories/helpers/hasContentCategoriesPermission.helper';
import { hasMediaQualityPermission } from '@/components/Targeting/MediaQualityAssuranceSystem/helpers/hasMediaQualityPermission.helper';
import TargetingProvider from '@/contexts/Targeting/TargetingProvider';
import { getCountriesValuesOptions } from '@/helpers/countriesOptions/countriesOptions.helper';
import { getUserBidModelsAndMargins } from '@/helpers/user/user.helpers';
import { useIsEntityInState } from '@/hooks/useIsEntityInState';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';
import { getSession } from '@/store/global/session/selectors';
import { getUser, reloadUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { useStore as useIOSettingsStore } from '@/store/local/ioSettings';
import { AdCreatives } from './Creatives/AdCreatives/AdCreatives';
import { getTargetingByMediaChannel } from './helpers/getTargetingByMediaChannel';
import { useHighlightSection } from './hooks/useHighlightSection';
import { useIoLineItemSchedules } from './hooks/useIoLineItemSchedules';
import { useItemRefs } from './hooks/useItemRefs';
import { useInsertionOrderFormContext } from './InsertionOrderForm.provider';
import { FormContainerStyled, FormStyled } from './InsertionOrderForm.styled';
import { MediaChannelSelectionSection } from './MediaChannelSelection/MediaChannelSelectionSection/MediaChannelSelectionSection';
import { MediaChannelSelectionStep } from './MediaChannelSelection/MediaChannelSelectionStep/MediaChannelSelectionStep';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { InsertionOrder } from 'sonata-static-storage/lib/entities';
import type { ContextualBiddingInterface } from 'sonata-static-storage/lib/schemas/campaign/contextualBidding.schema';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';
import type { ScreenGroupInterface } from 'sonata-static-storage/lib/schemas/campaign/screenGroup.schema';
import type { ZoneGroupInterface } from 'sonata-static-storage/lib/schemas/campaign/zoneGroup.schema';
import type { MediaTypeType } from 'sonata-static-storage/lib/schemas/mediaType/MediaType.types';
import type { FrequencyCapping } from 'sonata-static-storage/lib/schemas/multiFlight/frequencyCapping.schema';
import type { InsertionOrderFlight } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrderFlight.schema';
import type { PixelAssociatedEntity } from 'sonata-static-storage/lib/schemas/pixel/pixel.schema';
import type { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';
import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';
import type { IoSettingsStore } from '@/store/local/ioSettings';

type InsertionOrderFormRef = {
  scrollToSection: (sectionId: string) => void;
};

type Props = {
  className: string;
  isEditMode: boolean;
};

const InsertionOrderForm = forwardRef<InsertionOrderFormRef, Props>((props, ref) => {
  const { className, isEditMode } = props;
  const { formatMessage } = useIntl();
  const session = useGlobalStore(getSession);
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const baseCurrency = useGlobalStore(getBaseCurrency);
  const location = useLocation();
  const sessionId = session && !isEmpty(session) ? session?.toJSON().sessionId : '';
  const state = useIOSettingsStore() as IoSettingsStore;
  const {
    activeSubsection,
    errors,
    ioNumber,
    isSnliActive,
    list,
    name,
    screens,
    selectedSection,
    setActiveSubsection,
    setAds: setFormAds,
    setAdsDistribution: setFormAdsDistribution,
    setAdsPriceEnabled: setFormAdsPriceEnabled,
    setCountries: setFormCountries,
    setDco: setFormDco,
    setFlights,
    setIoNumber: setFormIoNumber,
    setList,
    setMediaChannel,
    setMediaType: setFormMediaType,
    setName: setFormName,
    setPricingModelConfig: setFormPricingModelConfig,
    setScheduleParting: setFormScheduleParting,
    setScreens,
    setTargeting: setFormTargeting,
    setUserFrequencyCapping: setFormUserFrequencyCapping,
    setZones,
    zones
  } = useInsertionOrderFormContext();
  const { getRef } = useItemRefs();
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollToSection } = useHighlightSection(getRef, setActiveSubsection, containerRef, activeSubsection);
  useImperativeHandle(ref, () => ({ scrollToSection }));

  const {
    _id,
    ads,
    attributionSettings,
    completed,
    contextualBidding,
    countries,
    dco,
    doohAudience,
    entity,
    flights,
    isLoading: isIoSettingsLoading,
    mediaType,
    pendingToCreate,
    pixels,
    pricingModelConfig,
    resetMapSave,
    saveIoSettings,
    scheduleParting,
    screenGroups,
    setAds,
    setAdsDistribution,
    setAdsPriceEnabled,
    setAttributionSettings,
    setContextualBidding,
    setCountries,
    setCustomEvents,
    setDco,
    setDoohAudience,
    setFlightSettings,
    setIoName,
    setIoNumber,
    setHasUnsavedMapChanges,
    setMediaChannelSelected,
    setMediaType,
    setPendingSaveSteps,
    setPixels,
    setPricingModelConfig,
    setSaveStep,
    setScheduleParting,
    setScreenGroups,
    setTargeting,
    setUserFrequencyCapping,
    setVenues,
    setZoneGroups,
    shouldMapSave,
    targeting,
    type: mediaChannelSelected,
    userFrequencyCapping,
    venues,
    zoneGroups
  } = state as IoSettingsStore;
  const userJSON = user.toJSON();
  const ioLineItems = useIoLineItemSchedules(String(user.get('id')), String(state._id), isEditMode);

  const bidPriceConfig = pricingModelConfig.bidModels[0].bid.pricingModel;
  const previousBidPriceConfig = usePrevious(bidPriceConfig);

  const handleAdsChange = (nextAds: InsertionOrder['ads']) => {
    setAds(nextAds);
    setFormAds(nextAds);
  };

  useDidUpdate(() => {
    if (
      previousBidPriceConfig !== undefined &&
      previousBidPriceConfig !== bidPriceConfig &&
      ads.some(ad => !ad.deleted && ad.visible && ad.bidPrice > 0)
    ) {
      handleAdsChange(ads.map(ad => (!ad.deleted && ad.visible && ad.bidPrice > 0 ? { ...ad, bidPrice: 0 } : ad)));
      showNotification(
        'map',
        formatMessage({
          defaultMessage: 'The pricing model has been updated. All ad prices have been reset to their default values.',
          id: 'ioSettings.pricingModelChanged'
        })
      );
    }
  }, [bidPriceConfig, previousBidPriceConfig]);

  useEffect(() => {
    return () => {
      taptap.entity = {
        model: undefined,
        validationErrors: undefined
      };
    };
  }, []);

  taptap.entity = {
    ...taptap.entity,
    model: entity.toJSON()
  };

  const showConversionTracking =
    checkUserResourcePermissions('campaignPixels', userJSON, userRoles) && mediaChannelSelected !== 'dooh';
  const showLQIIndex = checkUserResourcePermissions('lqi', userJSON, userRoles);
  const showContentCategories = hasContentCategoriesPermission(userJSON, userRoles);
  const showMediaQualityAssurance = hasMediaQualityPermission(userJSON, userRoles);
  const showDeviceTargeting = checkUserResourcePermissions('deviceIdImporter', userJSON, userRoles);
  const showAudienceTargeting =
    checkUserResourcePermissions('audienceThirdParty', userJSON, userRoles) ||
    checkUserResourcePermissions('audienceFirstParty', userJSON, userRoles) ||
    checkUserResourcePermissions('audienceAffinity', userJSON, userRoles) ||
    checkUserResourcePermissions('audienceCustom', userJSON, userRoles);
  const showRetargeting = checkUserResourcePermissions('retargetingSection', userJSON, userRoles);
  const showContextualMacros = mediaChannelSelected !== 'dooh';
  const showSupplySourceSection = checkUserResourcePermissions('supplySource', userJSON, userRoles);

  const getDefaultCountries = (): SelectorOptionPropsInterface[] => {
    if (isEditMode) {
      return getCountriesValuesOptions(countries);
    }
    return getCountriesValuesOptions(location.state?.campaignPlan?.countries);
  };

  const defaultCountries = getDefaultCountries();
  const mediaChannelOptions = [
    CampaignFamilyEnum.standard,
    ...(checkUserResourcePermissions('campaignType', userJSON, userRoles) ? [CampaignFamilyEnum.dooh] : []),
    ...(checkUserResourcePermissions('ctvDevicePlatform', userJSON, userRoles) ? [CampaignFamilyEnum.ctv] : [])
  ];

  const ioSettingsBoxClassName = cn({
    ioSettings__selectorBox: !mediaChannelSelected,
    'ioSettings__selectorBox ioSettings__selectorBox--hide': mediaChannelSelected
  });

  const handleMediaChannelChange = (value: CampaignFamilyEnum) => {
    const newMediaType = value === CampaignFamilyEnum.standard ? 'openRtb' : 'pmp';
    const newMarginsAndBidModels = getUserBidModelsAndMargins(
      mediaType,
      newMediaType,
      pricingModelConfig.bidModels,
      userJSON
    );
    const newPricingModelConfig = {
      ...structuredClone(pricingModelConfig),
      bidModels: newMarginsAndBidModels.bidModels,
      margin: newMarginsAndBidModels.margins
    };
    setMediaChannel(value);
    setMediaChannelSelected(value, { user: userJSON });
    setMediaType(newMediaType);
    setFormMediaType(newMediaType);
    setPricingModelConfig(newPricingModelConfig);
    setFormPricingModelConfig(newPricingModelConfig);
    setTargeting(getTargetingByMediaChannel(value));
  };

  if (!mediaChannelSelected && mediaChannelOptions.length === 1) {
    handleMediaChannelChange(mediaChannelOptions[0]);
  }

  if (
    !mediaChannelSelected &&
    location.state?.mediaChannel &&
    mediaChannelOptions.includes(location.state.mediaChannel)
  ) {
    handleMediaChannelChange(location.state.mediaChannel);
  }

  const handleFlightSettingsChange = (flightType: InsertionOrderFlight[]) => {
    setFlights(flightType);
    setFlightSettings(flightType);
  };

  const handlePricingModelChange = (newPricingModelConfig: PricingModelConfig) => {
    setPricingModelConfig(newPricingModelConfig);
    setFormPricingModelConfig(newPricingModelConfig);
  };

  const handleCountriesChange = (options: SelectorOptionPropsInterface[]) => {
    const countryId = options.map(option => option.id as string);
    setFormCountries(countryId);
    setCountries(countryId);
  };

  const handleIoNumberChange = (value: string | FileList | null) => {
    if (typeof value === 'string') {
      setFormIoNumber(value);
      setIoNumber(value);
    }
  };

  const handleIoNameChange = (value: string | FileList | null) => {
    if (typeof value === 'string') {
      setFormName(value);
      setIoName(value);
    }
  };

  const handleDcoChange = (newDco: InsertionOrder['dco']) => {
    setFormDco(newDco);
    setDco(newDco);
  };

  const handleAdsDistributionToggled = (newState: boolean) => {
    setAdsDistribution(newState);
    setFormAdsDistribution(newState);
  };

  const handleAdsPriceToggled = (newState: boolean) => {
    setAdsPriceEnabled(newState);
    setFormAdsPriceEnabled(newState);
  };

  const handleCustomEventsToggled = (newState: boolean) => {
    setCustomEvents(newState);
  };

  useEffect(() => {
    handleCountriesChange(defaultCountries);
  }, []);

  const handleSetTargeting = (newTargeting: TargetingBaseType) => {
    const cleanCarriers: Record<string, string>[] = [];
    if (newTargeting.carriers && !isEmpty(newTargeting.carriers)) {
      const carriers = compact(
        newTargeting.carriers.filter((carrierEntry: Record<string, any>) => !isEmpty(carrierEntry))
      ).flat();
      cleanCarriers.push(...(carriers as Record<string, any>[]));
    }
    const newCleanedTargeting: TargetingBaseType = {
      ...newTargeting,
      carriers: cleanCarriers
    };
    setFormTargeting(newCleanedTargeting);
    setTargeting(newCleanedTargeting);
  };

  const handleMapSaveComplete = () => {
    resetMapSave();
  };

  const mediaChannelSelectorDisabled =
    Boolean(mediaChannelSelected && isEditMode && state.completed) || mediaChannelOptions.length === 1;

  const totalBudget = flights.reduce((acc: number, flight: any) => acc + flight.goal.target, 0);

  const isStandard =
    mediaChannelSelected && isDigitalCampaign(mediaChannelSelected) && !isCTVCampaign(mediaChannelSelected);
  const isDooh = mediaChannelSelected && isDOOHCampaign(mediaChannelSelected);
  const isFinished = useIsEntityInState('insertionOrder', pendingToCreate ? undefined : _id, ['finished']);
  const isCTV = mediaChannelSelected && isCTVCampaign(mediaChannelSelected);
  const showMainSettingsSection = selectedSection === 'mainSettings';
  // The locations/screens section is the same physical block in the UI;
  // the active section name differs by campaign family (`'screens'` for
  // DOOH, `'locations'` otherwise). Treat both as "show the
  // locations-or-screens section".
  const showLocationsSection = selectedSection === 'locations' || selectedSection === 'screens';
  const showContextTargetingSection = selectedSection === 'contextTargeting';
  const showCreativesSection = selectedSection === 'creatives';

  const handleDevicePlatformChange = (
    devicePlatform: TargetingBaseType['devicePlatform'],
    unknownDeviceType: boolean
  ) => {
    const newTargeting = {
      ...targeting,
      devicePlatform,
      unknownIncluded: {
        ...targeting?.unknownIncluded,
        deviceType: unknownDeviceType
      }
    } as TargetingBaseType;
    setTargeting(newTargeting);
  };

  const handleCampaignMarketplaceChange = (newMediaType: MediaTypeType, prevMediaType: MediaTypeType) => {
    setMediaType(newMediaType);
    const newMarginsAndBidModels = getUserBidModelsAndMargins(
      prevMediaType,
      newMediaType,
      pricingModelConfig.bidModels,
      userJSON
    );
    const newPricingModelConfig = {
      ...structuredClone(pricingModelConfig),
      bidModels: newMarginsAndBidModels.bidModels,
      margin: newMarginsAndBidModels.margins
    };
    setPricingModelConfig(newPricingModelConfig);
    setFormPricingModelConfig(newPricingModelConfig);

    if (newMediaType === 'openRtb' && prevMediaType !== 'openRtb') {
      const newTargeting = {
        ...targeting,
        deals: targetingConstants.DEFAULT_DEALS_CONFIGURATION.deals
      } as TargetingBaseType;
      setTargeting(newTargeting);
      setFormTargeting(newTargeting);

      if (targeting?.deals?.length) {
        showNotification(
          'info',
          formatMessage({
            defaultMessage: 'Marketplace type changed to "Open".',
            id: 'ioSettings.marketplaceDealsReset.title'
          }),
          {
            description: formatMessage({
              defaultMessage:
                'Previously selected deals have been reset, as they don’t apply to this marketplace type.',
              id: 'ioSettings.marketplaceDealsReset.description'
            }),
            icon: 'fa-regular fa-circle-exclamation'
          }
        );
      }
    }
  };

  const handleMarketplaceDistributionChange = (premiumDistribution: number, openDistribution: number) => {
    const newPricingModelConfig = structuredClone(pricingModelConfig);
    newPricingModelConfig.bidModels = newPricingModelConfig.bidModels.map(bidModel => {
      if (bidModel.mediaType === 'pmp') {
        return { ...bidModel, distribution: premiumDistribution };
      }
      if (bidModel.mediaType === 'openRtb') {
        return { ...bidModel, distribution: openDistribution };
      }
      return bidModel;
    });
    setPricingModelConfig(newPricingModelConfig);
    setFormPricingModelConfig(newPricingModelConfig);
  };

  const handleSchedulePartingChange = (schedulePartingData: ScheduleParting) => {
    setFormScheduleParting(schedulePartingData);
    setScheduleParting(schedulePartingData);
  };

  const handleUserFrequencyCappingChange = (userFrequencyCappingData: FrequencyCapping) => {
    setFormUserFrequencyCapping(userFrequencyCappingData);
    setUserFrequencyCapping(userFrequencyCappingData);
  };

  const reloadUserToken = {};
  const handleReloadUser = async () => {
    await reloadUser(reloadUserToken);
  };

  return (
    <FormContainerStyled>
      {isDooh && !isSnliActive && !showLocationsSection ? <VolumetryAndForecastIO /> : null}
      <FormStyled
        className={`ioSettings ${className}`}
        isDooh={isDooh}
        ref={containerRef}
        showLocations={showLocationsSection}
      >
        <BoxV2
          borderColor="multiFlightSectionBorder"
          className={ioSettingsBoxClassName}
          id="ioSettings-boxMediaChannel"
        >
          <BoxV2.Body bgColor="boxBgColor01" padding="0">
            <MediaChannelSelectionStep
              defaultValue={CampaignFamilyEnum.standard}
              id="ioSettings-mediaChannelSelection"
              onSubmit={handleMediaChannelChange}
              options={mediaChannelOptions}
            />
          </BoxV2.Body>
        </BoxV2>
        {mediaChannelSelected && (
          <>
            {showMainSettingsSection && (
              <div ref={getRef('mainSettings')}>
                <TitleBar
                  className={classNames('ioSettings__titleBarSection', {
                    'ioSettings__titleBarSection--dooh': isDooh
                  })}
                  id="mediaChannelSelectionSection-title"
                  title={formatMessage({
                    defaultMessage: 'Main Settings',
                    id: 'ioSettings.mainSettingsTitle'
                  })}
                />
                <div className="ioSettings__mainSettingsContainer">
                  <div
                    className={cn('ioSettings__subsection', {
                      'ioSettings__subsection--selected': activeSubsection === 'mediaChannel'
                    })}
                    id="mediaChannel"
                    ref={getRef('mediaChannel')}
                  >
                    <MediaChannelSelectionSection
                      id="mediaChannelSelectionSection-box"
                      isDisabledChangeChannelButton={mediaChannelSelectorDisabled}
                      onChange={(value: CampaignFamilyEnum) => handleMediaChannelChange(value)}
                      options={mediaChannelOptions}
                      value={mediaChannelSelected}
                    />
                  </div>
                  <div
                    className={cn('ioSettings__subsection', {
                      'ioSettings__subsection--selected': activeSubsection === 'ioSettings'
                    })}
                    id="ioSettings"
                    ref={getRef('ioSettings')}
                  >
                    <SettingsSection
                      countries={countries}
                      defaultCountries={defaultCountries}
                      entity="insertionOrder"
                      errors={errors}
                      ioNumber={ioNumber}
                      name={name}
                      onCountriesChange={handleCountriesChange}
                      onIoNameChange={handleIoNameChange}
                      onIoNumberChange={handleIoNumberChange}
                    />
                  </div>
                  {isStandard ? (
                    <div
                      className={cn('ioSettings__subsection', {
                        'ioSettings__subsection--selected': activeSubsection === 'advertisingPlatform'
                      })}
                      id="advertisingPlatform"
                      ref={getRef('advertisingPlatform')}
                    >
                      <AdvertisingPlatform
                        budget={totalBudget || 100}
                        devicePlatform={targeting!.devicePlatform}
                        onDevicePlatformChange={handleDevicePlatformChange}
                        unknownIncluded={targeting?.unknownIncluded.deviceType || false}
                      />
                    </div>
                  ) : null}
                  <div
                    className={cn('ioSettings__subsection', {
                      'ioSettings__subsection--selected': activeSubsection === 'flightSettings'
                    })}
                    id="flightSettings"
                    ref={getRef('flightSettings')}
                  >
                    <FlightSettings
                      baseCurrency={baseCurrency}
                      errors={errors?.flights}
                      ioIsPublished={completed}
                      items={flights}
                      lineItems={ioLineItems}
                      onChange={handleFlightSettingsChange}
                    />
                  </div>
                </div>
                <div
                  className={cn('ioSettings__subsection', 'ioSettings__scheduleAndDistribution', {
                    'ioSettings__subsection--selected': activeSubsection === 'schedulingDistribution'
                  })}
                  id="schedulingDistribution"
                  ref={getRef('schedulingDistribution')}
                >
                  <ScheduleAndDistribution
                    contextualBidding={entity.toJSON().contextualBidding}
                    entityType="insertionOrder"
                    errors={errors}
                    flights={flights}
                    id={entity.toJSON()._id}
                    isDooh={isDooh}
                    onSchedulePartingChange={handleSchedulePartingChange}
                    onUserFrequencyCappingChange={handleUserFrequencyCappingChange}
                    scheduleParting={scheduleParting}
                    user={userJSON}
                    userFrequencyCapping={userFrequencyCapping}
                    userRoles={userRoles}
                  />
                </div>
                {!isDooh && !isCTV && userJSON.mediaTypes.length > 1 ? (
                  <div className="ioSettings__campaignMarketplaceContainer">
                    <div
                      className={cn('ioSettings__subsection', {
                        'ioSettings__subsection--selected': activeSubsection === 'campaignMarketplace'
                      })}
                      id="campaignMarketplace"
                      ref={getRef('campaignMarketplace')}
                    >
                      <CampaignMarketplace
                        marketPlace={mediaType}
                        onCampaignMarketplaceChange={handleCampaignMarketplaceChange}
                      />
                    </div>
                    {mediaType === 'hybrid' ? (
                      <MarketplaceDistribution
                        bidModels={pricingModelConfig.bidModels}
                        budget={totalBudget || 100}
                        onChange={handleMarketplaceDistributionChange}
                      />
                    ) : null}
                  </div>
                ) : null}
                <div
                  className={cn('ioSettings__subsection', 'ioSettings__pricingModel', {
                    'ioSettings__subsection--selected': activeSubsection === 'pricingModel'
                  })}
                  id="pricingModel"
                  ref={getRef('pricingModel')}
                >
                  <PricingModel
                    currency={baseCurrency}
                    errors={errors?.pricingModelConfig}
                    id="prcingModel-id"
                    mediaType={MediaTypeEnum[mediaType as keyof typeof MediaTypeEnum]}
                    onChange={handlePricingModelChange}
                    pricingModelConfig={pricingModelConfig}
                    type={mediaChannelSelected}
                    user={userJSON}
                    userRoles={userRoles}
                  />
                </div>
              </div>
            )}
            <div className="ioSettings__map" ref={getRef('locations')}>
              <MultiFlightMap
                activeSubsection={activeSubsection}
                ads={ads}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                attributionSettings={attributionSettings}
                countries={countries}
                currentSection={selectedSection}
                entityId={entity.toJSON()._id!}
                entityIsDraft={!entity.toJSON().completed}
                entityType="insertionOrder"
                id="multiFlightMap"
                isLoading={isIoSettingsLoading}
                key={mediaChannelSelected}
                list={list}
                mapStyle="streets"
                onChangeAds={handleAdsChange}
                onChangeAttributionSettings={setAttributionSettings}
                onChangeItems={{
                  screens: setScreens,
                  zones: setZones
                }}
                onChangeScreenGroups={setScreenGroups}
                onChangeZoneGroups={setZoneGroups}
                onPendingSaveStepsChange={setPendingSaveSteps}
                onRefreshUser={handleReloadUser}
                onSaveComplete={handleMapSaveComplete}
                onSaveStepChange={setSaveStep}
                onUnsavedMapChangesChange={setHasUnsavedMapChanges}
                onVenuesChange={setVenues}
                saveSettings={saveIoSettings}
                screenGroups={screenGroups}
                setList={setList}
                shouldMapSave={shouldMapSave}
                targeting={targeting as TargetingBaseType}
                type={mediaChannelSelected}
                user={user.toJSON()}
                userRoles={userRoles}
                venues={venues}
                volumetryAndForecast={isDooh && !isSnliActive ? <VolumetryAndForecastIO /> : undefined}
                zoneGroups={zoneGroups}
                zones={zones}
              />
            </div>
            {showContextTargetingSection && (
              <div ref={getRef('contextTargeting')}>
                <div className="ioSettings__targeting">
                  <div className="ioSettings__targetingContainer">
                    <TitleBar
                      className={classNames('ioSettings__titleBar', {
                        'ioSettings__titleBar--dooh': isDooh
                      })}
                      id="ioSettings-targetingTitle"
                      title={formatMessage({
                        defaultMessage: 'Context & Targeting',
                        id: 'ioSettings.targetingTitle'
                      })}
                    />
                    <TargetingSection
                      description={formatMessage({
                        defaultMessage:
                          'Define exactly who will see your ads based on user profiles, past interactions, and demographic data.',
                        id: 'ioSettings.targetingSection.description.audienceAndUsers'
                      })}
                      label={formatMessage({
                        defaultMessage: 'Audience & Users',
                        id: 'ioSettings.targetingSection.audienceAndUsers'
                      })}
                    />
                    <div
                      className={cn('ioSettings__subsection', {
                        'ioSettings__subsection--selected': activeSubsection === 'demography'
                      })}
                      id="demography"
                      ref={getRef('demography')}
                    >
                      <Demography
                        country={countries?.[0]}
                        doohAudience={doohAudience}
                        familyType={mediaChannelSelected}
                        isReadOnly={isFinished}
                        onChange={handleSetTargeting}
                        onDoohAudienceChange={setDoohAudience}
                        targeting={targeting}
                        user={user.toJSON()}
                        userRoles={userRoles}
                      />
                    </div>
                    {!isDooh && (
                      <>
                        {showRetargeting && (
                          <div id="retargeting" ref={getRef('retargeting')}>
                            <TargetingProvider
                              familyType={mediaChannelSelected}
                              onChange={handleSetTargeting}
                              showDeals={isDooh}
                              targeting={targeting as TargetingBaseType}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            >
                              <Retargeting
                                className={cn('ioSettings__subsection', {
                                  'ioSettings__subsection--selected': activeSubsection === 'retargeting'
                                })}
                                onSetZones={setZones}
                                zones={zones}
                              />
                            </TargetingProvider>
                          </div>
                        )}
                        {showAudienceTargeting && (
                          <div
                            className={cn('ioSettings__subsection', {
                              'ioSettings__subsection--selected': activeSubsection === 'audienceTargeting'
                            })}
                            id="audienceTargeting"
                            ref={getRef('audienceTargeting')}
                          >
                            <AudienceTargeting
                              baseCurrency={baseCurrency}
                              onChange={handleSetTargeting}
                              targeting={targeting}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            />
                          </div>
                        )}
                      </>
                    )}
                    {!isDooh && showDeviceTargeting && (
                      <div
                        className={cn('ioSettings__subsection', {
                          'ioSettings__subsection--selected': activeSubsection === 'deviceTargeting'
                        })}
                        id="deviceTargeting"
                        ref={getRef('deviceTargeting')}
                      >
                        <DeviceTargeting
                          campaignId={entity.toJSON()._id}
                          onChange={handleSetTargeting}
                          sessionId={sessionId}
                          targeting={targeting}
                          user={user.toJSON()}
                          userRoles={userRoles}
                        />
                      </div>
                    )}
                    <TargetingSection
                      description={formatMessage({
                        defaultMessage:
                          'Select the technical specifications for your campaign, including device types, connections, and specific inventory sources.',
                        id: 'ioSettings.targetingSection.description.technologyAndSupply'
                      })}
                      label={formatMessage({
                        defaultMessage: 'Technology & Supply',
                        id: 'ioSettings.targetingSection.technologyAndSupply'
                      })}
                    />
                    {!isDooh && (
                      <>
                        <div
                          className={cn('ioSettings__subsection', {
                            'ioSettings__subsection--selected': activeSubsection === 'mobileDevicesConnectivity'
                          })}
                          id="mobileDevicesConnectivity"
                          ref={getRef('mobileDevicesConnectivity')}
                        >
                          <MobileDeviceAndConnectivity
                            countries={countries}
                            familyType={mediaChannelSelected}
                            onChange={handleSetTargeting}
                            targeting={targeting}
                            user={user.toJSON()}
                            userRoles={userRoles}
                          />
                        </div>
                        {showSupplySourceSection && (
                          <div
                            className={cn('ioSettings__subsection', {
                              'ioSettings__subsection--selected': activeSubsection === 'supplySource'
                            })}
                            id="supplySource"
                            ref={getRef('supplySource')}
                          >
                            <SupplySource
                              baseCurrency={baseCurrency}
                              error={errors?.targeting?.adExchanges}
                              flightsGoals={flights.map((flight: any) => flight.goal)}
                              onChange={handleSetTargeting}
                              targeting={targeting}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div
                      className={cn('ioSettings__subsection', {
                        'ioSettings__subsection--selected': activeSubsection === 'appMarketplace'
                      })}
                      id="appMarketplace"
                      ref={getRef('appMarketplace')}
                    >
                      <TargetingProvider
                        familyType={mediaChannelSelected}
                        onChange={handleSetTargeting}
                        showDeals={mediaType === 'pmp' || mediaType === 'hybrid'}
                        targeting={targeting as TargetingBaseType}
                        user={user.toJSON()}
                        userRoles={userRoles}
                      >
                        <AppAndSiteMarketplace
                          entityId={entity.toJSON()._id!}
                          goals={[{ target: totalBudget, timeFrame: 'overall', type: 'budget' }]}
                          id={`appMarketplace${entity.toJSON()._id!}`}
                          pricingModelConfig={pricingModelConfig}
                          sessionId={sessionId}
                        />
                      </TargetingProvider>
                    </div>
                    <TargetingSection
                      description={formatMessage({
                        defaultMessage:
                          'Control where and when your ads appear by targeting specific content themes, channels, or real-world conditions.',
                        id: 'ioSettings.targetingSection.description.contextAndEnvironment'
                      })}
                      label={formatMessage({
                        defaultMessage: 'Context & Environment',
                        id: 'ioSettings.targetingSection.contextAndEnvironment'
                      })}
                    />
                    {!isDooh && (
                      <>
                        <div
                          className={cn('ioSettings__subsection', {
                            'ioSettings__subsection--selected': activeSubsection === 'appSiteCategories'
                          })}
                          id="appSiteCategories"
                          ref={getRef('appSiteCategories')}
                        >
                          <AppAndSitesCategories
                            onChange={handleSetTargeting}
                            targeting={targeting}
                            user={user.toJSON()}
                            userRoles={userRoles}
                          />
                        </div>
                        <div
                          className={cn('ioSettings__subsection', {
                            'ioSettings__subsection--selected': activeSubsection === 'appSiteChannel'
                          })}
                          id="appSiteChannel"
                          ref={getRef('appSiteChannel')}
                        >
                          <AppAndSiteChannel
                            onChange={handleSetTargeting}
                            targeting={targeting}
                            user={user.toJSON()}
                            userRoles={userRoles}
                          />
                        </div>
                        {showContentCategories && (
                          <div
                            className={cn('ioSettings__subsection', {
                              'ioSettings__subsection--selected': activeSubsection === 'contentCategories'
                            })}
                            id="contentCategories"
                            ref={getRef('contentCategories')}
                          >
                            <ContentCategories
                              baseCurrency={baseCurrency}
                              error={errors?.targeting?.contextualTargeting}
                              onChange={handleSetTargeting}
                              targeting={targeting}
                              totalBudget={totalBudget}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div
                      className={cn('ioSettings__subsection', {
                        'ioSettings__subsection--selected': activeSubsection === 'temperatureAndWeather'
                      })}
                      id="temperatureAndWeather"
                      ref={getRef('temperatureAndWeather')}
                    >
                      <TemperatureAndWeather
                        error={errors?.targeting?.temperatureAndWeather?.temperature}
                        onChange={handleSetTargeting}
                        targeting={targeting}
                        user={user.toJSON()}
                        userRoles={userRoles}
                      />
                    </div>
                    <div
                      className={cn('ioSettings__subsection', {
                        'ioSettings__subsection--selected': activeSubsection === 'contextualBidding'
                      })}
                      id="contextualBidding"
                      ref={getRef('contextualBidding')}
                    >
                      <ContextualBidding
                        campaign={entity.toJSON()}
                        contextualBidding={contextualBidding as ContextualBiddingInterface}
                        currencyInfo={baseCurrency}
                        entityType="insertionOrder"
                        goal={entity.toJSON().flights[0]?.goal?.target}
                        groupsConfig={{
                          groups:
                            mediaChannelSelected === CampaignFamilyEnum.dooh
                              ? (screenGroups as ScreenGroupInterface<string>[])
                              : (zoneGroups as ZoneGroupInterface<string>[]),
                          items:
                            mediaChannelSelected === CampaignFamilyEnum.dooh
                              ? (screens as GeoDataAndScreensInteface[])
                              : (zones as Zone[])
                        }}
                        id="multiFlightContextualBidding"
                        onChange={setContextualBidding}
                        user={user.toJSON()}
                        userRoles={userRoles}
                      />
                    </div>
                    {!isDooh && (
                      <>
                        {(showLQIIndex || showMediaQualityAssurance) && (
                          <TargetingSection
                            description={formatMessage({
                              defaultMessage:
                                'Protect your brand and ensure high performance by applying traffic quality filters and brand safety assurance systems.',
                              id: 'ioSettings.targetingSection.description.qualityAndBrandSafety'
                            })}
                            label={formatMessage({
                              defaultMessage: 'Quality & Brand Safety',
                              id: 'ioSettings.targetingSection.qualityAndBrandSafety'
                            })}
                          />
                        )}
                        {showLQIIndex && (
                          <div
                            className={cn('ioSettings__subsection', {
                              'ioSettings__subsection--selected': activeSubsection === 'locationQualityIndexes'
                            })}
                            id="locationQualityIndexes"
                            ref={getRef('locationQualityIndexes')}
                          >
                            <LQIQualityAssurance
                              familyType={mediaChannelSelected}
                              onChange={handleSetTargeting}
                              targeting={targeting}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            />
                          </div>
                        )}
                        {showMediaQualityAssurance && (
                          <div
                            className={cn('ioSettings__subsection', {
                              'ioSettings__subsection--selected': activeSubsection === 'mediaQualityAssurance'
                            })}
                            id="mediaQualityAssurance"
                            ref={getRef('mediaQualityAssurance')}
                          >
                            <MediaQualityAssuranceSystem
                              currency={baseCurrency}
                              onChange={handleSetTargeting}
                              targeting={targeting}
                              user={user.toJSON()}
                              userRoles={userRoles}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            {showCreativesSection ? (
              <div className="ioSettings__creatives" ref={getRef('creatives')}>
                <TitleBar
                  className={classNames('ioSettings__titleBarSection', {
                    'ioSettings__titleBarSection--dooh': isDooh
                  })}
                  id="ioSettings-creativesTitle"
                  title={formatMessage({
                    defaultMessage: 'Creatives',
                    id: 'ioSettings.creatives.title'
                  })}
                />
                <div className="ioSettings__creativesContainer">
                  <div
                    className={cn('ioSettings__subsection', {
                      'ioSettings__subsection--selected': activeSubsection === 'adCreatives'
                    })}
                    id="adCreatives"
                    ref={getRef('adCreatives')}
                  >
                    <AdCreatives
                      entity={entity.toJSON()}
                      entityType="insertionOrder"
                      errors={errors}
                      onAdsChange={handleAdsChange}
                      onAdsDistributionToggled={handleAdsDistributionToggled}
                      onAdsPriceToggled={handleAdsPriceToggled}
                      onCustomEventsToggled={handleCustomEventsToggled}
                      onVenuesChange={setVenues}
                      savedBaseline={state.savedBaseline}
                      screens={screens ?? []}
                      user={user.entity}
                      userRoles={userRoles}
                      venues={venues}
                    />
                  </div>
                  {showConversionTracking && (
                    <div
                      className={cn('ioSettings__subsection', {
                        'ioSettings__subsection--selected': activeSubsection === 'conversionTracking'
                      })}
                      id="conversionTracking"
                      ref={getRef('conversionTracking')}
                    >
                      <ConversionTracking
                        id="ioConversionTracking"
                        onPixelsChange={setPixels}
                        pixels={pixels as unknown as PixelAssociatedEntity[]}
                      />
                    </div>
                  )}
                  <div
                    className={cn('ioSettings__subsection', {
                      'ioSettings__subsection--selected': activeSubsection === 'contextualSettings'
                    })}
                    id="contextualSettings"
                    ref={getRef('contextualSettings')}
                  >
                    <DynamicCreativeOptimization
                      ads={ads}
                      campaign={entity.toJSON()}
                      countries={countries}
                      dco={dco!}
                      error={
                        errors?.dco?.scenarios &&
                        (Object.values(errors.dco.scenarios as object) as any[]).find(s => s?.ads)?.ads
                      }
                      id="dynamicCreativeOptimization"
                      onDcoChange={handleDcoChange}
                      user={user.toJSON()}
                      userRoles={userRoles}
                    />
                  </div>
                  {showContextualMacros && (
                    <div
                      className={cn('ioSettings__subsection', {
                        'ioSettings__subsection--selected': activeSubsection === 'contextualMacros'
                      })}
                      id="contextualMacros"
                      ref={getRef('contextualMacros')}
                    >
                      <ContextualMacros
                        ads={ads}
                        countries={countries}
                        entityId={entity.toJSON()._id}
                        entityIsActive={entity.toJSON().completed}
                        id="ioContextualMacros"
                        onAdsChange={handleAdsChange}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </>
        )}
      </FormStyled>
    </FormContainerStyled>
  );
});

export default InsertionOrderForm;
